import { useApolloClient } from '@apollo/client';
import { Meta, theme } from 'aimo-ui';
import ParkingContext from 'context/ParkingContext';
import { isBefore, startOfMinute } from 'date-fns';
import { READ_PARKING_SESSION_RETRY_STATUS } from 'graphql/queries/readParkingSessionRetryStatus';
import useExternalPaymentMethods from 'hooks/useExternalPaymentMethods';
import { useKeyboardVisible } from 'hooks/useKeyboardVisible';
import useOngoingParkingSession from 'hooks/useOngoingParkingSession';
import useParkingPricingInfo from 'hooks/useParkingPricingInfo';
import useParkingZoneChangeCountry from 'hooks/useParkingZoneChangeCountry';
import useReadPurchasedPrepaidTickets from 'hooks/useReadPurchasedPrepaidTickets';
import useSelectedCompanyBenefit from 'hooks/useSelectedCompanyBenefit';
import useSelectedParkingVehicle from 'hooks/useSelectedParkingVehicle';
import useSuggestedPricingEstimates from 'hooks/useSuggestedPricingEstimates';
import React, { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useActiveElementStore } from 'stores/activeElementStore';
import { useParkingStore } from 'stores/parkingStore';
import { useParkzoneStore } from 'stores/parkzoneStore';
import { usePaymentStore } from 'stores/paymentStore';
import styled from 'styled-components/native';
import { ScreenContainer, StyledWrapper } from 'styles/ContainerStyles';
import { ParkingLayoutProps } from 'types/common';
import { Query, QueryReadParkingSessionArgs } from 'types/generatedSchemaTypes';
import { isAndroid } from 'utils/deviceUtils';
import { waitForExternalPaymentResult } from 'utils/paymentUtils';
import { doAllPermitsHaveFutureStartDates } from 'utils/permitUtils';
import { getMaxParkingTime } from 'utils/timeUtils';
import ParkingCapacityInfo from './ParkingCapacityInfo';
import Tariff from './tariff/Tariff';
import TimeSelection from './timeSelection';
import InfoCarousel from './timeSelection/InfoCarousel';

const CenteredWrapper = styled.View<{ mb: number; mt: number }>`
  align-items: center;
  margin-bottom: ${({ mb }) => mb}px;
  margin-top: ${({ mt }) => mt}px;
`;

const ParkingConditionsCustomEndTime: React.FC<ParkingLayoutProps> = () => {
  const { parkingZoneResult, salesOrdersForZoneAndVehicle } =
    useContext(ParkingContext);
  const { ongoingParkingSessionRefetch } = useOngoingParkingSession(
    false,
    true
  );

  const {
    parkingConditions,
    selectedParkingSegment,
    selectedParkingVehiclePlate,
    setParkingConditions,
  } = useParkingStore((state) => state);
  const selectedVehicle = useSelectedParkingVehicle(
    false,
    undefined,
    selectedParkingVehiclePlate
  );
  const keyboardVisible = useKeyboardVisible();
  const externalPaymentComplete = usePaymentStore(
    (s) => s.externalPaymentFlowCompleted
  );
  const { t } = useTranslation();
  useParkingZoneChangeCountry();
  const times = {
    parkingAllowedFrom: selectedParkingSegment?.parkingAllowedFrom,
    parkingAllowedUntil: selectedParkingSegment?.parkingAllowedUntil,
  };
  let minDate: Date | undefined;
  let maxDate: Date | undefined;
  if (times?.parkingAllowedFrom && times.parkingAllowedUntil) {
    const allowedFromDate = new Date(times.parkingAllowedFrom);
    if (isBefore(allowedFromDate, Date.now())) {
      minDate = allowedFromDate;
    }
    maxDate = new Date(times.parkingAllowedUntil);
  }

  const startTime = startOfMinute(
    times?.parkingAllowedFrom ? new Date(times.parkingAllowedFrom) : new Date()
  );

  const {
    corporateAccountTransactionFee,
    parkingCorporateAccountDiscountPercent,
  } = useSelectedCompanyBenefit(parkingConditions.selectedParkingZoneId);

  const { handleExternalPaymentComplete } = useExternalPaymentMethods();
  const client = useApolloClient();
  const suggestedDurations = useSuggestedPricingEstimates(
    parkingConditions.selectedParkingZoneId,
    startTime,
    corporateAccountTransactionFee || undefined,
    parkingConditions?.parkingZoneCountryCode,
    parkingCorporateAccountDiscountPercent
  );

  useEffect(() => {
    let left = false;
    const handleReturnFromExternalPayment = async () => {
      if (!externalPaymentComplete) {
        return;
      }
      const result = await waitForExternalPaymentResult(
        usePaymentStore.getState().externalPaymentSessionId,
        async (sessionId) => {
          const { data } = await client.query<
            Query,
            QueryReadParkingSessionArgs
          >({
            query: READ_PARKING_SESSION_RETRY_STATUS,
            variables: { sessionId },
            fetchPolicy: 'no-cache',
          });
          return data?.readParkingSession?.paymentState;
        },
        () => left
      );
      if (result === 'left') {
        return;
      }
      if (result === 'notFailed') {
        try {
          await ongoingParkingSessionRefetch();
        } catch (error) {
          console.error('refetch error:', error);
        }
        handleExternalPaymentComplete();
        return;
      }
      // Parking did not start (or nothing was waiting for Swish): stop the
      // spinner but keep vehicle, payment method and end time for a retry.
      const paymentStore = usePaymentStore.getState();
      paymentStore.setExternalPaymentFlowCompleted(false);
      paymentStore.setExternalPaymentFlowLoading(false);
      paymentStore.setExternalPaymentSessionId(null);
    };
    handleReturnFromExternalPayment().catch((error) => {
      console.error('external payment return error:', error);
    });
    return () => {
      left = true;
    };
  }, [externalPaymentComplete]);

  const capacity = parkingZoneResult?.data?.parkZone?.capacityV2;

  const parkingZoneType = useActiveElementStore(
    (state) => state.activeElementType
  );
  const selectedTab = useParkzoneStore((state) => state.selectedParkingZoneTab);
  const isParkAndCharge =
    parkingZoneType === 'PARK_AND_CHARGE' && selectedTab === 'PARK_AND_CHARGE';
  const isAnpr =
    parkingZoneResult?.data?.parkZone?.parkingMethods?.anpr === true;

  const maxParkingTime = getMaxParkingTime(
    times?.parkingAllowedFrom || '',
    times?.parkingAllowedUntil || ''
  );

  const { pricingInfoError, pricingInfoLoading } = useParkingPricingInfo(
    parkingConditions.selectedParkingZoneId,
    true
  );

  const { hasPrepaidForSelectedVehicle } = useReadPurchasedPrepaidTickets({
    selectedVehicle: selectedVehicle,
  });

  const hasPrepaidTickets = hasPrepaidForSelectedVehicle.length > 0;

  const hasPrepaidTicketsOrPermits =
    hasPrepaidTickets || !!salesOrdersForZoneAndVehicle?.length;

  const hasSalesOrder = !!salesOrdersForZoneAndVehicle?.length;
  const userPaysParking = hasSalesOrder
    ? salesOrdersForZoneAndVehicle.some(
        (salesOrder) =>
          salesOrder.orderLines?.[0]?.pmc?.doShortTermPriceCalculation
      )
    : true;

  const areAllPermitsFutureDated = doAllPermitsHaveFutureStartDates(
    salesOrdersForZoneAndVehicle
  );

  const setEndTime = (date: Date) => {
    setParkingConditions({ endTime: startOfMinute(date) });
  };

  const setIsValidEndTime = (isValid: boolean) => {
    setParkingConditions({ isValidEndTime: isValid });
  };

  const renderTimeSelection = () => (
    <CenteredWrapper mb={keyboardVisible && isAndroid ? 0 : 20} mt={0}>
      <TimeSelection
        testID={'time-selection'}
        parkingZoneUid={parkingConditions.selectedParkingZoneId}
        parkingZoneCountryCode={
          parkingConditions?.parkingZoneCountryCode || 'FI'
        }
        minDate={minDate}
        maxDate={maxDate}
        startTime={startTime}
        endTime={parkingConditions.endTime}
        setEndTimeCallback={setEndTime}
        setIsValidEndTimeCallback={setIsValidEndTime}
      />
      {maxParkingTime && (
        <StyledWrapper mb={0} center>
          <Meta center testID="max-parking-time" color={theme.colors.gray300}>
            {isParkAndCharge ? t('evCharging.maximumChargingTime') : ''}
            {t('parking.maxParkingTimeFromNow', { amount: maxParkingTime })}
          </Meta>
        </StyledWrapper>
      )}
    </CenteredWrapper>
  );

  const renderContent = () => {
    if (hasPrepaidTickets) {
      return <></>;
    }

    const isPricingInfoReady =
      !pricingInfoLoading && !pricingInfoError && userPaysParking;
    if (isAnpr) {
      return;
    }
    if (areAllPermitsFutureDated || isPricingInfoReady) {
      return renderTimeSelection();
    }
  };

  if (!parkingConditions.selectedParkingZoneId) {
    return null;
  }

  return (
    <ScreenContainer
      mb={0}
      justify={hasPrepaidTicketsOrPermits ? 'flex-start' : 'space-between'}>
      <StyledWrapper mb={theme.normalising.heightPixel(15)}>
        <Tariff padding={10} />
        {capacity && <ParkingCapacityInfo capacity={capacity} />}
        <InfoCarousel
          suggestedDurations={suggestedDurations}
          currentEndTime={parkingConditions.endTime}
        />
      </StyledWrapper>
      {renderContent()}
    </ScreenContainer>
  );
};

export default ParkingConditionsCustomEndTime;
